import React, { useEffect, useState } from 'react';
import { saveCrisisSimulation, getCrisisSimulationAiInsightsFromPayload } from '../services/crisisSimulatorApi';

const CrisisSimulator = () => {
  const [currentMineral, setCurrentMineral] = useState('lithium');
  const [selectedTimeframe, setSelectedTimeframe] = useState(0);
  const [mineralData, setMineralData] = useState({});
  const [crisisUiState, setCrisisUiState] = useState({
    lastRunResults: null,
    aiInsights: null,
    saveStatus: null,
    insightsLoading: false,
  });
  const lastRunResults = crisisUiState.lastRunResults;
  const aiInsights = crisisUiState.aiInsights;
  const saveStatus = crisisUiState.saveStatus;
  const insightsLoading = crisisUiState.insightsLoading;
  const setLastRunResults = (v) => setCrisisUiState((s) => ({ ...s, lastRunResults: v }));
  const setAiInsights = (v) => setCrisisUiState((s) => ({ ...s, aiInsights: v }));
  const setSaveStatus = (v) => setCrisisUiState((s) => ({ ...s, saveStatus: v }));
  const setInsightsLoading = (v) => setCrisisUiState((s) => ({ ...s, insightsLoading: v }));

  // Mineral data configuration (static, used for dropdown and initial state)
  const mineralDataConfig = {
    lithium: {
      name: 'Lithium',
      suppliers: [
        { country: 'China', share: 45, capacity: 180000 },
        { country: 'Australia', share: 25, capacity: 120000 },
        { country: 'Chile', share: 20, capacity: 95000 },
        { country: 'Argentina', share: 10, capacity: 40000 },
      ],
      alternatives: [
        { country: 'Canada', capacity: 30000, timeToMarket: 18, risk: 'Low' },
        { country: 'Brazil', capacity: 25000, timeToMarket: 24, risk: 'Medium' },
        { country: 'Zimbabwe', capacity: 35000, timeToMarket: 36, risk: 'High' },
      ],
      totalImports: 1000000,
      stockpileDays: 45,
      economicValue: 2.3
    },
    cobalt: {
      name: 'Cobalt',
      suppliers: [
        { country: 'DRC', share: 67, capacity: 95000 },
        { country: 'China', share: 18, capacity: 25000 },
        { country: 'Canada', share: 8, capacity: 11000 },
        { country: 'Australia', share: 7, capacity: 9500 },
      ],
      alternatives: [
        { country: 'Morocco', capacity: 8000, timeToMarket: 12, risk: 'Medium' },
        { country: 'Finland', capacity: 12000, timeToMarket: 24, risk: 'Low' },
        { country: 'Philippines', capacity: 15000, timeToMarket: 30, risk: 'Medium' },
      ],
      totalImports: 142000,
      stockpileDays: 30,
      economicValue: 1.8
    },
    nickel: {
      name: 'Nickel',
      suppliers: [
        { country: 'Indonesia', share: 48, capacity: 1000000 },
        { country: 'Philippines', share: 12, capacity: 250000 },
        { country: 'Russia', share: 10, capacity: 210000 },
        { country: 'Canada', share: 8, capacity: 170000 },
        { country: 'New Caledonia', share: 5, capacity: 100000 },
        { country: 'Australia', share: 5, capacity: 100000 },
        { country: 'Others', share: 12, capacity: 250000 },
      ],
      alternatives: [
        { country: 'Brazil', capacity: 120000, timeToMarket: 18, risk: 'Medium' },
        { country: 'Madagascar', capacity: 80000, timeToMarket: 24, risk: 'High' },
        { country: 'Papua New Guinea', capacity: 70000, timeToMarket: 24, risk: 'Medium' },
      ],
      totalImports: 1500000,
      stockpileDays: 40,
      economicValue: 3.1
    },
    ree: {
      name: 'Rare Earth Elements',
      suppliers: [
        { country: 'China', share: 89, capacity: 240000 },
        { country: 'Myanmar', share: 6, capacity: 16000 },
        { country: 'USA', share: 3, capacity: 8000 },
        { country: 'Australia', share: 2, capacity: 5000 },
      ],
      alternatives: [
        { country: 'Greenland', capacity: 12000, timeToMarket: 48, risk: 'Medium' },
        { country: 'South Africa', capacity: 8000, timeToMarket: 36, risk: 'High' },
        { country: 'Brazil', capacity: 6000, timeToMarket: 42, risk: 'Medium' },
      ],
      totalImports: 270000,
      stockpileDays: 60,
      economicValue: 0.8
    },
    graphite: {
      name: 'Graphite',
      suppliers: [
        { country: 'China', share: 78, capacity: 850000 },
        { country: 'Mozambique', share: 12, capacity: 130000 },
        { country: 'Madagascar', share: 6, capacity: 65000 },
        { country: 'Brazil', share: 4, capacity: 43000 },
      ],
      alternatives: [
        { country: 'Canada', capacity: 45000, timeToMarket: 18, risk: 'Low' },
        { country: 'Norway', capacity: 35000, timeToMarket: 24, risk: 'Low' },
        { country: 'Tanzania', capacity: 28000, timeToMarket: 36, risk: 'High' },
      ],
      totalImports: 1088000,
      stockpileDays: 90,
      economicValue: 1.2
    }
  };

  const timelineStops = [0, 14, 30, 60, 90, 180];

  useEffect(() => {
    // Initialize mineral data with disruption states
    const initializedData = {};
    Object.keys(mineralDataConfig).forEach(mineral => {
      initializedData[mineral] = {
        ...mineralDataConfig[mineral],
        suppliers: mineralDataConfig[mineral].suppliers.map(supplier => ({
          ...supplier,
          disrupted: false,
          events: []
        }))
      };
    });
    setMineralData(initializedData);
  }, []);


  const handleMineralChange = (mineral) => {
    setCurrentMineral(mineral);
    resetOutputs();
  };

  const toggleDisruption = (supplierIndex) => {
    setMineralData(prev => {
      const newData = { ...prev };
      const mineralData = { ...newData[currentMineral] };
      const suppliers = [...mineralData.suppliers];
      const supplier = { ...suppliers[supplierIndex] };
      
      supplier.disrupted = !supplier.disrupted;
      
      if (supplier.disrupted && supplier.events.length === 0) {
        supplier.events = [{ type: 'trade', level: 75 }];
      } else if (!supplier.disrupted) {
        supplier.events = [];
      }
      
      suppliers[supplierIndex] = supplier;
      mineralData.suppliers = suppliers;
      newData[currentMineral] = mineralData;
      
      return newData;
    });
  };

  const selectScenario = (supplierIndex, type, level) => {
    setMineralData(prev => {
      const newData = { ...prev };
      const mineralData = { ...newData[currentMineral] };
      const suppliers = [...mineralData.suppliers];
      const supplier = { ...suppliers[supplierIndex] };
      const events = [...supplier.events];
      
      const existingEvent = events.find(e => e.type === type);
      
      if (existingEvent) {
        supplier.events = events.filter(e => e.type !== type);
      } else {
        supplier.events = [...events, { type, level }];
      }
      
      suppliers[supplierIndex] = supplier;
      mineralData.suppliers = suppliers;
      newData[currentMineral] = mineralData;
      
      return newData;
    });
  };

  const runSimulation = () => {
    const data = mineralData[currentMineral];
    let totalLost = 0;
    const disrupted = [];
    const disruptedSuppliers = [];

    data.suppliers.forEach(supplier => {
      if (supplier.disrupted && supplier.events && supplier.events.length) {
        const combined = 1 - supplier.events.reduce((acc, e) => acc * (1 - e.level / 100), 1);
        totalLost += supplier.share * combined;
        disrupted.push({ share: supplier.share });
        disruptedSuppliers.push({
          country: supplier.country,
          share: supplier.share,
          events: supplier.events || [],
        });
      }
    });

    let altPct = 0;
    data.alternatives.forEach(alt => {
      altPct += (alt.capacity / data.totalImports) * 100;
    });

    const supplyGap = Math.max(0, totalLost - altPct);
    const priceImpact = calcPriceImpact(supplyGap, selectedTimeframe);
    const ttc = calcTimeToCrisis(data, disrupted, selectedTimeframe);
    const econ = (supplyGap / 100) * data.economicValue;

    const score = alt => alt.capacity * (alt.risk === 'Low' ? 3 : alt.risk === 'Medium' ? 2 : 1) / alt.timeToMarket;
    const alternativesRanked = [...(data.alternatives || [])].sort((a, b) => score(b) - score(a));

    setLastRunResults({
      supplyGap,
      priceImpact,
      timeToCrisis: ttc === Infinity ? null : ttc,
      economicImpact: econ,
      disruptedSuppliers,
      alternativesRanked,
    });
    setAiInsights(null);

    const supplyGapEl = document.getElementById('supplyGap');
    const priceImpactEl = document.getElementById('priceImpact');
    const timeToCrisisEl = document.getElementById('timeToCrisis');
    const economicImpactEl = document.getElementById('economicImpact');
    if (supplyGapEl) supplyGapEl.textContent = Math.round(supplyGap) + '%';
    if (priceImpactEl) priceImpactEl.textContent = '+' + Math.round(priceImpact) + '%';
    if (timeToCrisisEl) timeToCrisisEl.textContent = (ttc === Infinity ? '∞' : (ttc > 0 ? ttc + ' days' : 'Immediate'));
    if (economicImpactEl) economicImpactEl.textContent = '$' + econ.toFixed(1) + 'B';

    renderAlternatives();
  };

  const calcPriceImpact = (gap, timeframe) => {
    let base = 0;
    if (gap > 30) base = 100;
    else if (gap > 20) base = 60;
    else if (gap > 10) base = 30;
    else if (gap > 5) base = 15;

    let multiplier = 1;
    if (timeframe === 0) multiplier = 1.5;
    else if (timeframe <= 30) multiplier = 1.3;
    else if (timeframe <= 90) multiplier = 1.1;
    else multiplier = 0.8;

    return Math.round(base * multiplier);
  };

  const calcTimeToCrisis = (data, disruptedArr, timeframe) => {
    if (timeframe === 0) return 0;
    if (disruptedArr.length === 0) return Infinity;
    
    let base = data.stockpileDays + timeframe;
    const totalShare = disruptedArr.reduce((s, x) => s + x.share, 0);
    const multiplier = totalShare > 50 ? 0.7 : totalShare > 30 ? 0.8 : totalShare > 15 ? 0.9 : 1;
    
    return Math.round(base * multiplier);
  };


  const renderAlternatives = () => {
    const list = document.getElementById('alternativesList');
    if (!list) return; // Add null check
    
    const data = mineralData[currentMineral];
    if (!data || !data.alternatives) return; // Add data check
    
    list.innerHTML = '';
    
    const score = alt => alt.capacity * (alt.risk === 'Low' ? 3 : alt.risk === 'Medium' ? 2 : 1) / alt.timeToMarket;
    const sorted = [...data.alternatives].sort((a, b) => score(b) - score(a));
    
    sorted.forEach(alt => {
      const div = document.createElement('div');
      div.className = 'alternative-card';
      div.innerHTML = `
        <div class="alt-country">${alt.country}</div>
        <div>Capacity: ${(alt.capacity / 1000).toFixed(0)}K tonnes/yr</div>
        <div>Time to market: ${alt.timeToMarket} months</div>
        <div>Risk: ${alt.risk}</div>
      `;
      list.appendChild(div);
    });
  };

  const resetOutputs = () => {
    const supplyGap = document.getElementById('supplyGap');
    const priceImpact = document.getElementById('priceImpact');
    const timeToCrisis = document.getElementById('timeToCrisis');
    const economicImpact = document.getElementById('economicImpact');
    const alternativesList = document.getElementById('alternativesList');
    if (supplyGap) supplyGap.textContent = '0%';
    if (priceImpact) priceImpact.textContent = '+0%';
    if (timeToCrisis) timeToCrisis.textContent = '∞';
    if (economicImpact) economicImpact.textContent = '$0B';
    if (alternativesList) alternativesList.innerHTML = '';
    setSelectedTimeframe(0);
    setLastRunResults(null);
    setAiInsights(null);
    setSaveStatus(null);
  };

  const buildPayload = () => {
    if (!lastRunResults || !mineralData[currentMineral]) return null;
    const data = mineralData[currentMineral];
    return {
      mineral_key: currentMineral,
      mineral_name: (mineralDataConfig[currentMineral] || {}).name || currentMineral,
      selected_timeframe_days: selectedTimeframe,
      supply_gap_pct: lastRunResults.supplyGap,
      price_impact_pct: lastRunResults.priceImpact,
      time_to_crisis_days: lastRunResults.timeToCrisis,
      economic_impact_b: lastRunResults.economicImpact,
      input_snapshot: {
        name: data.name,
        totalImports: data.totalImports,
        stockpileDays: data.stockpileDays,
        economicValue: data.economicValue,
        suppliers: data.suppliers,
        alternatives: data.alternatives,
      },
      disrupted_suppliers: lastRunResults.disruptedSuppliers,
      alternatives_ranked: lastRunResults.alternativesRanked,
    };
  };

  const handleSaveSimulation = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaveStatus('saving');
    try {
      await saveCrisisSimulation(payload);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  const handleGetAiInsights = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setInsightsLoading(true);
    setAiInsights(null);
    try {
      const res = await getCrisisSimulationAiInsightsFromPayload(payload);
      setAiInsights(res.insights || res);
    } catch (e) {
      setAiInsights({ summary: e.message || 'Failed to load AI insights.', recommendations: [], risk_level: 'unknown' });
    } finally {
      setInsightsLoading(false);
    }
  };

  const scenarioOptions = [
    { k: 'trade', name: 'Trade Restrictions', lvl: 75 },
    { k: 'conflict', name: 'Armed Conflict', lvl: 90 },
    { k: 'natural', name: 'Natural Disaster', lvl: 60 },
    { k: 'sanctions', name: 'Economic Sanctions', lvl: 85 },
    { k: 'shipping', name: 'Shipping Disruption', lvl: 40 },
    { k: 'policy', name: 'Policy Changes', lvl: 50 },
  ];

  const scenarioNames = {
    trade: 'Trade Restrictions',
    conflict: 'Armed Conflict',
    natural: 'Natural Disaster',
    sanctions: 'Economic Sanctions',
    shipping: 'Shipping Disruption',
    policy: 'Policy Changes'
  };

  const currentData = mineralData[currentMineral] || { suppliers: [], alternatives: [] };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-5">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900 dark:text-blue-100 mb-2">
            Critical Minerals Crisis Simulator
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Model supply chain disruptions and predict their economic and strategic impacts
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5">
          {/* Instructions */}
          <div className="bg-white dark:bg-gray-800 border-l-4 border-blue-500 rounded-lg p-5 shadow-sm">
            <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">How to use</h2>
            <ol className="list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300">
              <li>Select a <strong>mineral</strong> below.</li>
              <li>Toggle one or more <strong>supplier countries</strong> to <em>Disrupted</em>.</li>
              <li>Choose one or more <strong>disruption types</strong> for each selected country.</li>
              <li>Pick a <strong>timeline</strong> (Immediate / 14 / 30 / 60 / 90 / 180 days).</li>
              <li>Click <strong>Run Crisis Simulation</strong> (top-right of Impact Analysis).</li>
            </ol>
          </div>

          {/* Mineral selector */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
            <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">Select Critical Mineral</h2>
            <div className="grid grid-cols-5 gap-2">
              {Object.keys(mineralDataConfig).map((mineral) => (
                <button
                  key={mineral}
                  onClick={() => handleMineralChange(mineral)}
                  className={`p-3 border-2 rounded-lg font-bold cursor-pointer transition-all ${
                    currentMineral === mineral
                      ? 'bg-cyan-400 border-blue-500 text-white'
                      : 'border-gray-200 dark:border-gray-600 text-blue-900 dark:text-blue-100 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900'
                  }`}
                >
                  {mineralDataConfig[mineral].name}
                </button>
              ))}
            </div>
          </div>

          {/* Supplier disruptions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
            <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">Supplier Disruptions</h2>
            <div className="space-y-3">
              {currentData.suppliers?.map((supplier, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <div className="font-bold text-blue-900 dark:text-blue-100">{supplier.country}</div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">{supplier.share}% market share</div>
                    </div>
                    <button
                      onClick={() => toggleDisruption(idx)}
                      className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors ${
                        supplier.disrupted ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          supplier.disrupted ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {supplier.disrupted && (
                    <>
                      <div className="font-bold text-blue-800 dark:text-blue-200 mb-2">Disruption types</div>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        {scenarioOptions.map((option) => (
                          <button
                            key={option.k}
                            onClick={() => selectScenario(idx, option.k, option.lvl)}
                            className={`p-2 border rounded-lg text-sm cursor-pointer transition-all ${
                              supplier.events?.some(e => e.type === option.k)
                                ? 'border-blue-800 dark:border-blue-300 bg-blue-100 dark:bg-blue-900'
                                : 'border-gray-200 dark:border-gray-600 bg-blue-50 dark:bg-blue-800 hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-700'
                            }`}
                          >
                            {option.name} ({option.lvl}%)
                          </button>
                        ))}
                      </div>
                      {supplier.events && supplier.events.length > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Selected: {supplier.events.map(e => `${scenarioNames[e.type]} (${e.level}%)`).join(' • ')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Crisis timeline controls + chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
            <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">Crisis Timeline</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              {timelineStops.map((days) => (
                <button
                  key={days}
                  onClick={() => setSelectedTimeframe(days)}
                  className={`px-3 py-2 border-2 rounded-lg font-bold cursor-pointer transition-all ${
                    selectedTimeframe === days
                      ? 'bg-teal-400 border-blue-500 text-gray-900'
                      : 'border-gray-200 dark:border-gray-600 text-blue-900 dark:text-blue-100 hover:border-teal-400'
                  }`}
                >
                  {days === 0 ? 'Immediate' : days + ' days'}
                </button>
              ))}
            </div>
            <div className="relative h-36 border border-gray-200 dark:border-gray-600 rounded-lg p-5">
              {/* Base timeline bar */}
              <div className="absolute left-5 right-5 top-1/2 h-1 bg-gray-300 dark:bg-gray-600 rounded-full transform -translate-y-1/2"></div>
              
              {/* Progress bar with gradient */}
              <div
                id="timelineProgress"
                className="absolute left-5 top-1/2 h-1 bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-500 rounded-full transform -translate-y-1/2 transition-all duration-500 ease-out"
                style={{ 
                  width: selectedTimeframe === 0 ? '100%' : `${(selectedTimeframe / 180) * 100}%`
                }}
              ></div>
              
              {/* Timeline markers */}
              {timelineStops.map((days) => {
                const percentage = (days / 180) * 100;
                return (
                  <div
                    key={days}
                    className="absolute top-1/2 transform -translate-y-1/2 text-center"
                    style={{ 
                      left: days === 0 ? '0%' : days === 180 ? '100%' : `${percentage}%`,
                      transform: days === 0 ? 'translateY(-50%)' : days === 180 ? 'translateY(-50%) translateX(-100%)' : 'translateY(-50%) translateX(-50%)'
                    }}
                  >
                    <div className="w-0.5 h-7 bg-gray-400 dark:bg-gray-500 mx-auto mb-1"></div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                      {days === 0 ? 'Today' : days + 'd'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Impact + Alternatives */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blue-900 dark:text-blue-100">Impact Analysis</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={runSimulation}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg font-bold cursor-pointer hover:bg-blue-600 transition-colors"
                >
                  Run Crisis Simulation
                </button>
                {lastRunResults && (
                  <>
                    <button
                      onClick={handleSaveSimulation}
                      disabled={saveStatus === 'saving'}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg font-bold cursor-pointer hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                      {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save simulation'}
                    </button>
                    <button
                      onClick={handleGetAiInsights}
                      disabled={insightsLoading}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold cursor-pointer hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {insightsLoading ? 'Loading...' : 'Get AI insights'}
                    </button>
                  </>
                )}
              </div>
            </div>
            {saveStatus === 'error' && (
              <p className="text-red-600 dark:text-red-400 text-sm mb-2">Failed to save. Check backend connection.</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="text-center">
                <div className="text-sm text-blue-800 dark:text-blue-200 uppercase tracking-wider mb-1">Supply Shortage</div>
                <div id="supplyGap" className="text-2xl font-bold text-gray-900 dark:text-white">0%</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-800 dark:text-blue-200 uppercase tracking-wider mb-1">Price Impact</div>
                <div id="priceImpact" className="text-2xl font-bold text-gray-900 dark:text-white">+0%</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-800 dark:text-blue-200 uppercase tracking-wider mb-1">Time to Crisis</div>
                <div id="timeToCrisis" className="text-2xl font-bold text-gray-900 dark:text-white">∞</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-800 dark:text-blue-200 uppercase tracking-wider mb-1">Economic Impact</div>
                <div id="economicImpact" className="text-2xl font-bold text-gray-900 dark:text-white">$0B</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
            <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">Alternative Supply Sources</h2>
            <div id="alternativesList" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Alternatives will be populated by JavaScript */}
            </div>
          </div>

          {/* AI Insights (Claude-based simulation analysis) */}
          {(aiInsights || insightsLoading) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm border-l-4 border-indigo-500">
              <h2 className="text-lg font-bold text-indigo-900 dark:text-indigo-100 mb-3">AI Insights</h2>
              {insightsLoading && (
                <p className="text-gray-600 dark:text-gray-400">Analyzing simulation data...</p>
              )}
              {aiInsights && !insightsLoading && (
                <div className="space-y-4">
                  {aiInsights.risk_level && (
                    <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: aiInsights.risk_level === 'Critical' ? '#b91c1c' : aiInsights.risk_level === 'High' ? '#c2410c' : aiInsights.risk_level === 'Medium' ? '#b45309' : '#15803d' }}>
                      Risk level: {aiInsights.risk_level}
                    </p>
                  )}
                  {aiInsights.summary && (
                    <p className="text-gray-700 dark:text-gray-300">{aiInsights.summary}</p>
                  )}
                  {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-2">Recommendations</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm">
                        {aiInsights.recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrisisSimulator;
